import React, { FC, memo, useCallback, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useButton, useCurrency, useI18n, useLogger } from '~Hooks';
import useItemModifications, {
  FormattedModificationGroup,
} from '~Hooks/ItemModifications/useItemModifications';
import { LocalVariation } from '~Types';
import { useExpressCartActions } from '../../../Providers/ExpressCartProvider';
import { useExpressProductLibrary } from '../../../Providers/ExpressProductLibraryProvider';
import { useEposTheme } from '../../../Providers/ThemeProvider';
import { categoryIconSrc } from '../../../Utils/categoryIcons';
import GlassPill from '../../../Components/GlassPill';
import arrowBackIcon from '../../../../../Assets/icons/arrow_back.svg';
import { CartLineEdit, EposMenuItem, EposMenuUpsell } from '../../../types';
import { DetailStepChip } from './types';
import ModificationGroupSection from './ModificationGroupSection';
import StepProgress from './StepProgress';
import AgeRestrictionNotice from './AgeRestrictionNotice';
import UpsellCard from './UpsellCard';
import ItemHero from './ItemHero';
import CommentField from './CommentField';

/** Fills the detail pane - CartPanel stays docked beside/below it either way. */
const Pane = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Scroller = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  /* Room for the pinned add bar, plus the gap it now floats above the edge by. */
  padding-bottom: 152px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

/** Floats over the hero image, top left. */
const BackSlot = styled.div`
  position: absolute;
  top: 16px;
  left: 24px;
  z-index: 1;
`;

const Content = styled.div`
  padding: 24px 36px;
  display: flex;
  flex-direction: column;
  gap: 21px;

  /** No hero above - clear the floating back pill instead. */
  &.is-heroless {
    padding-top: 80px;
  }
`;

/** Each upsell group is its own page, sliding in from the right. Keyed per group so it replays. */
const Step = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  animation: eposStepIn 0.3s ease-out both;

  @keyframes eposStepIn {
    from {
      opacity: 0;
      transform: translate3d(24px, 0, 0);
    }
    to {
      opacity: 1;
      transform: translate3d(0, 0, 0);
    }
  }
`;

/** Big friendly question as the page title, "1 / 3" tucked in on the right. */
const StepHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const StepTitle = styled.h3`
  margin: 0;
  font-size: 31px;
  font-weight: 800;
  letter-spacing: -0.7px;
  color: var(--eposText);
`;

const StepCounter = styled.span`
  flex: 0 0 auto;
  padding-top: 8px;
  font-size: 12px;
  font-weight: 700;
  opacity: 0.5;
  color: var(--eposText);
`;

/** Photo cards, three across, so the add-ons look like food rather than a form. */
const UpsellGrid = styled.div`
  display: grid;
  /* Tokens in styles/index.css - tighter on narrow screens. */
  grid-template-columns: repeat(auto-fill, minmax(var(--eposUpsellGridMin), 1fr));
  gap: var(--eposUpsellGridGap);
  padding: 3px;
`;

/**
 * Pinned over the scrolling content, a little clear of the bottom edge - not
 * flush against it, which on a portrait screen reads as too easy to miss or
 * mistake for outside the pane, and would sit under a device's home-indicator
 * safe area with nothing but its own padding protecting it.
 */
const AddBar = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: max(20px, env(safe-area-inset-bottom, 0px));
  padding: 18px 36px 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  /* No backdrop - the button floats over the content on its own. */
  background: transparent;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`;

/**
 * The primary action. Filled in the venue color when it commits something
 * (add, continue with picks). With `is-quiet` - an upsell page where nothing
 * was picked - it turns into a calm outlined "No thanks", so the photo cards
 * above are the loudest thing on the page.
 */
const AddButton = styled.button`
  flex: 1;
  min-width: 0;
  min-height: 88px;
  appearance: none;
  border: 2px solid var(--eposButton);
  border-radius: 20px;
  padding: 0 32px;
  display: flex;
  align-items: center;
  /* Centered like every other button here - label and amount sit together as one group. */
  justify-content: center;
  gap: 12px;
  cursor: pointer;
  font-family: var(--eposFontFamily);
  font-size: 24px;
  font-weight: 700;
  background: var(--eposButton);
  color: var(--eposButtonText);
  transition: transform 0.15s ease-out, opacity 0.2s ease-out, background-color 0.2s ease-out,
    color 0.2s ease-out;

  &.is-touching {
    transform: scale(0.98);
  }

  /* Filled - the bar behind is transparent. */
  &.is-quiet {
    border-color: var(--eposBorderColor);
    background: var(--eposSurface);
    color: var(--eposText);
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

interface Props {
  item: EposMenuItem;
  /** Navigation only - slides the pane back. Adding goes through the cart hook. */
  onBack: () => void;
  /**
   * Set when this pane was opened via "Ändra"/"Edit" on a cart line instead
   * of from the menu: prefills every step with what's already picked, and
   * commit() edits those cart lines in place instead of adding new ones.
   */
  editing?: CartLineEdit;
}

/** One page after the details: a modification group (only with oneModificationGroupPerPage) or an upsell group. */
type DetailPage =
  | { kind: 'modification'; group: FormattedModificationGroup }
  | { kind: 'upsell'; upsell: EposMenuUpsell };

/**
 * Full item view in the right-hand pane of the sheet. The photo hero (title,
 * price, allergens, description) stays at the top on every page. Details
 * first (modification groups), then - if the item has upsells - one page per
 * upsell group. With the venue's `oneModificationGroupPerPage` set, each
 * modification group becomes a page of its own too, ahead of the upsells, and
 * a StepProgress row under the hero shows where the shopper is. Nothing reaches the cart until the
 * shopper is completely done: then the parent line is added (or, editing an
 * existing line, its variations and upsells are replaced) and every picked
 * upsell attaches to that exact cart line id through `addUpsell`. More of the same item is added from the cart, so this
 * pane has no quantity control - one tap adds exactly one.
 *
 * Render budget: this pane uses the cart *actions* hook only, so it never
 * re-renders because the cart changed. Every callback handed to a card is
 * stable - the changing state is read through refs - so a tap re-renders
 * exactly the card whose quantity moved.
 *
 * Mount with `key={item.id}` so the selection state starts fresh per item.
 */
const ItemDetail: FC<Props> = ({ item, onBack, editing }) => {
  const { i18n } = useI18n();
  const logger = useLogger('epos-item-detail');
  const currency = useCurrency();
  const cartActions = useExpressCartActions();
  const productLibrary = useExpressProductLibrary();
  const eposTheme = useEposTheme();
  /** Backoffice: every modification group gets a page of its own instead of stacking on the details page. */
  const perPage = !!eposTheme.oneModificationGroupPerPage;

  const modifications = useItemModifications(
    item.modificationGroups,
    editing?.variationIds,
    editing?.removedVariationIds,
  );

  /**
   * Which groups sit on the details page. All of them normally; in per-page
   * mode only the removable-ingredient groups ("no onion") stay there, since
   * they are about the item itself rather than a choice to make.
   */
  const detailsGroups = useMemo(
    () =>
      perPage
        ? modifications.modificationGroups.filter((group) => group.isRemovableIngredientGroup)
        : modifications.modificationGroups,
    [perPage, modifications.modificationGroups],
  );

  /** The pages after details, in order: the remaining modification groups (per-page mode only), then upsells. */
  const pages = useMemo<DetailPage[]>(
    () => [
      ...(perPage
        ? modifications.modificationGroups
            .filter((group) => !group.isRemovableIngredientGroup)
            .map((group): DetailPage => ({ kind: 'modification', group }))
        : []),
      ...item.upsells.map((upsell): DetailPage => ({ kind: 'upsell', upsell })),
    ],
    [perPage, modifications.modificationGroups, item.upsells],
  );
  const hasPages = pages.length > 0;

  /**
   * In per-page mode the details page only earns its place when it has
   * removable-ingredient groups to show - otherwise the flow opens straight on
   * the first page (the shrunken item header still carries title and allergens).
   */
  const skipDetails = perPage && hasPages && detailsGroups.length === 0;

  /** -1 is the details page; 0..n-1 indexes `pages`. */
  const [step, setStep] = useState(skipDetails ? 0 : -1);

  /**
   * Upsell item id -> how many the shopper has picked. Nothing touches the cart
   * until the last page is done - backing out leaves the cart as it was.
   * Prefilled from the line being edited, if any.
   */
  const [upsellPicks, setUpsellPicks] = useState<Record<string, number>>(
    () => editing?.upsellPicks ?? {},
  );

  /** Mirrors of state for the stable callbacks below - read at tap time, never captured. */
  const upsellPicksRef = useRef(upsellPicks);
  upsellPicksRef.current = upsellPicks;

  const stepRef = useRef(step);
  stepRef.current = step;

  const pagesRef = useRef(pages);
  pagesRef.current = pages;

  /** The shopper's comment - owned by CommentField, read at commit. Never state, so typing renders nothing. */
  const commentRef = useRef(editing?.comment ?? '');
  /** Set by the first commit - the pane stays tappable while it slides out, so later taps must not add again. */
  const committedRef = useRef(false);
  const onSelectVariationRef = useRef(modifications.onSelectVariation);
  onSelectVariationRef.current = modifications.onSelectVariation;

  const currentPage = step >= 0 ? pages[step] : null;
  const currentUpsell = currentPage?.kind === 'upsell' ? currentPage.upsell : null;
  const currentGroup = currentPage?.kind === 'modification' ? currentPage.group : null;
  const isLastStep = step === pages.length - 1;
  /** Without per-page mode the upsell pages are a quick add-on pick - no hero or progress row needed. */
  const showHero = perPage || !currentUpsell;

  /** Base price plus every selected extra. */
  const unitTotal = useMemo(
    () =>
      item.priceValue +
      modifications.selectedVariations.reduce((sum, variation) => sum + variation.price, 0),
    [item.priceValue, modifications.selectedVariations],
  );

  /** What the picks on the current upsell page add up to - shown as a delta on the button. */
  const stepDelta = useMemo(() => {
    if (!currentUpsell) {
      return 0;
    }
    return currentUpsell.items.reduce(
      (sum, upsellItem) => sum + (upsellPicks[upsellItem.id] ?? 0) * upsellItem.priceValue,
      0,
    );
  }, [currentUpsell, upsellPicks]);

  const stepHasPicks = useMemo(
    () =>
      !!currentUpsell &&
      currentUpsell.items.some((upsellItem) => (upsellPicks[upsellItem.id] ?? 0) > 0),
    [currentUpsell, upsellPicks],
  );

  /**
   * What the StepProgress row draws per page: the group's icon until a pick is
   * made, then the picks' photos as a collage (falling back to the icon when
   * none has one), with the count on the badge when more than one is picked.
   */
  const chips = useMemo<DetailStepChip[]>(
    () =>
      pages.map((page) => {
        if (page.kind === 'modification') {
          const picked = page.group.variations.filter((variation) => variation.selected);
          return {
            id: page.group.id,
            label: page.group.title,
            iconSrc: categoryIconSrc(page.group.icon),
            imageUrls: picked.map((variation) => variation.imageUrl ?? '').filter(Boolean),
            pickCount: picked.length,
          };
        }
        const picked = page.upsell.items.filter(
          (upsellItem) => (upsellPicks[upsellItem.id] ?? 0) > 0,
        );
        return {
          id: page.upsell.id,
          label: page.upsell.name,
          iconSrc: page.upsell.iconSrc,
          imageUrls: picked.map((upsellItem) => upsellItem.imageUrl).filter(Boolean),
          pickCount: picked.reduce(
            (sum, upsellItem) => sum + (upsellPicks[upsellItem.id] ?? 0),
            0,
          ),
        };
      }),
    [pages, upsellPicks],
  );

  /**
   * The hook hands out a new onSelectVariation every render; this stable
   * wrapper keeps every memoized VariationOption from re-rendering per tap.
   */
  const handleSelectVariation = useCallback((id: string) => onSelectVariationRef.current(id), []);

  /**
   * The one moment the cart changes: the parent line, then every picked
   * upsell attached to that exact line id - not the item id, not the menu entry.
   */
  const commit = useCallback(() => {
    if (committedRef.current) {
      logger.warn('Ignored repeated commit from detail view', { itemId: item.id });
      return;
    }
    committedRef.current = true;
    const picks = upsellPicksRef.current;
    const variations: LocalVariation[] = modifications.selectedVariations.map((variation) => ({
      id: variation.id,
      name: variation.name,
      price: variation.price,
      asItem: variation.asItem ? { id: variation.asItem.id } : undefined,
    }));
    const upsellCount = Object.values(picks).reduce((sum, count) => sum + count, 0);

    if (editing) {
      logger.info('Editing cart line from detail view', {
        itemId: item.id,
        parentCount: editing.ids.length,
        variationCount: variations.length,
        upsellCount,
      });
      const upsellPicksList = item.upsells.flatMap((upsell) =>
        upsell.items
          .map((upsellItem) => ({ item: upsellItem, count: picks[upsellItem.id] ?? 0 }))
          .filter((pick) => pick.count > 0),
      );
      cartActions.editMenuItem(
        editing.ids,
        item,
        variations,
        modifications.removedVariations,
        upsellPicksList,
        commentRef.current,
      );
      onBack();
      return;
    }

    logger.info('Adding item from detail view', {
      itemId: item.id,
      variationCount: variations.length,
      upsellCount,
    });

    const parentCartId = cartActions.addMenuItem(
      item,
      variations,
      modifications.removedVariations,
      commentRef.current,
    );

    item.upsells.forEach((upsell) =>
      upsell.items.forEach((upsellItem) => {
        for (let count = picks[upsellItem.id] ?? 0; count > 0; count -= 1) {
          cartActions.addUpsell(upsellItem, parentCartId);
        }
      }),
    );

    onBack();
  }, [
    item,
    editing,
    modifications.selectedVariations,
    modifications.removedVariations,
    cartActions,
    onBack,
  ]);

  /** Details page button: add straight away, or move on to the first page. */
  const handleAdd = useCallback(() => {
    if (!hasPages) {
      commit();
      return;
    }
    logger.info('Continuing to detail pages', { itemId: item.id, perPage });
    setStep(0);
  }, [hasPages, commit, item.id, perPage]);

  /** "−" on an upsell card. Stable - reads the picks via ref. */
  const handleDecrementUpsell = useCallback((upsellItem: EposMenuItem) => {
    setUpsellPicks((picks) => ({
      ...picks,
      [upsellItem.id]: Math.max((picks[upsellItem.id] ?? 0) - 1, 0),
    }));
  }, []);

  /**
   * "+" on an upsell card. In a single-select group the previous choice is
   * dropped first, so the group never holds more than one. Stable for the
   * same reason as above.
   */
  const handleIncrementUpsell = useCallback((upsellItem: EposMenuItem) => {
    const page = stepRef.current >= 0 ? pagesRef.current[stepRef.current] : null;
    if (page?.kind !== 'upsell') {
      return;
    }
    const upsell = page.upsell;
    setUpsellPicks((picks) => {
      const next = { ...picks };
      if (!upsell.enableMultiSelect) {
        upsell.items.forEach((other) => {
          next[other.id] = 0;
        });
      }
      next[upsellItem.id] = (picks[upsellItem.id] ?? 0) + 1;
      return next;
    });
  }, []);

  /** Next page, or back to the menu after the last one. */
  const handleContinuePage = useCallback(() => {
    if (isLastStep) {
      commit();
      return;
    }
    setStep((current) => current + 1);
  }, [isLastStep, commit]);

  /** Back pill: previous page, or leave the item entirely. */
  const handleBack = useCallback(() => {
    if (stepRef.current > 0) {
      setStep((current) => current - 1);
      return;
    }
    onBack();
  }, [onBack]);

  /** Something is picked in the modification group shown as a page. */
  const groupHasPicks = !!currentGroup?.variations.some((variation) => variation.selected);

  /**
   * Details: "Continue" when pages follow, otherwise "Add". Modification
   * pages: "Continue", or "Add"/"Save" on the last one. Upsell pages: "No
   * thanks" while nothing is picked, else "Continue" / "Done".
   */
  const buttonLabel = (() => {
    if (!currentPage) {
      if (hasPages) {
        return i18n('App.Continue');
      }
      return i18n(editing ? 'App.Save' : 'App.Add');
    }

    if (currentGroup) {
      return isLastStep ? i18n(editing ? 'App.Save' : 'App.Add') : i18n('App.Continue');
    }

    if (!stepHasPicks) {
      return i18n('Express.Upsell.NoThanks');
    }

    return i18n(isLastStep ? 'App.Done' : 'App.Continue');
  })();

  /** Details and modification pages show the item price with its extras; upsell pages show what the picks add. */
  const buttonAmount = (() => {
    if (!currentUpsell) {
      return currency.format(unitTotal);
    }
    return stepDelta > 0 ? `+ ${currency.format(stepDelta)}` : null;
  })();

  /** Outlined, not filled: nothing picked on an upsell page, or on an optional modification page. */
  const quiet = (!!currentUpsell && !stepHasPicks) || (!!currentGroup && !groupHasPicks);

  /** The groups shown on the details page are all satisfied. */
  const detailsValid = useMemo(() => detailsGroups.every((group) => group.valid), [detailsGroups]);

  /**
   * Details gates on the groups shown there; a modification page gates on its
   * own group; upsell pages never block.
   */
  const buttonDisabled = currentGroup ? !currentGroup.valid : !currentPage && !detailsValid;

  const { touching: addTouching, disabled: addD, disabledToast: addT, ...addButton } = useButton({
    onClick: currentPage ? handleContinuePage : handleAdd,
    disabled: buttonDisabled,
  });

  const buttonClassName = [addTouching && 'is-touching', quiet && 'is-quiet']
    .filter(Boolean)
    .join(' ');

  return (
    <Pane data-testid="epos-item-detail">
      <BackSlot>
        <GlassPill
          size="small"
          icon={arrowBackIcon}
          onClick={handleBack}
          testId="epos-detail-back">
          {i18n('Express.Back')}
        </GlassPill>
      </BackSlot>

      <Scroller>
        {/*
          The hero stays put on every page. On the details page it shows the
          price; on the step pages it switches to step mode with the progress
          row along its bottom edge instead.
        */}
        {showHero && (
          <ItemHero
            title={item.title}
            description={item.description}
            price={currentPage ? undefined : currency.format(item.priceValue)}
            imageUrl={item.imageUrl}
            allergyTags={item.allergyTags}
            progress={currentPage ? <StepProgress chips={chips} currentIndex={step} /> : undefined}
          />
        )}

        {currentGroup ? (
          <Content>
            <Step key={currentGroup.id}>
              <ModificationGroupSection
                group={currentGroup}
                onSelect={handleSelectVariation}
                asPage
              />
              {/* The kitchen comment closes the flow - only on the last page. */}
              {isLastStep && <CommentField commentRef={commentRef} />}
            </Step>
          </Content>
        ) : currentUpsell ? (
          <Content className={showHero ? undefined : 'is-heroless'}>
            <Step key={currentUpsell.id}>
              <StepHeader>
                <StepTitle>{currentUpsell.name}</StepTitle>
                <StepCounter data-testid="epos-upsell-step">
                  {step + 1} / {pages.length}
                </StepCounter>
              </StepHeader>
              <UpsellGrid>
                {currentUpsell.items.map((upsellItem) => (
                  <UpsellCard
                    key={upsellItem.id}
                    item={upsellItem}
                    multiSelect={currentUpsell.enableMultiSelect}
                    quantity={upsellPicks[upsellItem.id] ?? 0}
                    unavailable={productLibrary.outOfStockIds.has(upsellItem.id)}
                    onIncrement={handleIncrementUpsell}
                    onDecrement={handleDecrementUpsell}
                  />
                ))}
              </UpsellGrid>
              {/* The kitchen comment closes the flow - only on the last page. */}
              {isLastStep && <CommentField commentRef={commentRef} />}
            </Step>
          </Content>
        ) : (
          <Content>
            {item.ageRestricted && <AgeRestrictionNotice />}

            {/* All groups, or in per-page mode just the removable-ingredient ones - see `detailsGroups`. */}
            {detailsGroups.map((group) => (
              <ModificationGroupSection
                key={group.id}
                group={group}
                onSelect={handleSelectVariation}
                collapsible={!perPage}
              />
            ))}

            {/* Free text for the kitchen - the value lives in commentRef, not state. Here only when no pages follow. */}
            {!hasPages && <CommentField commentRef={commentRef} />}
          </Content>
        )}
      </Scroller>

      <AddBar>
        <AddButton
          {...addButton}
          data-testid="epos-detail-primary"
          disabled={buttonDisabled}
          className={buttonClassName || undefined}>
          <span>{buttonLabel}</span>
          {buttonAmount && <span>{buttonAmount}</span>}
        </AddButton>
      </AddBar>
    </Pane>
  );
};

export default memo(ItemDetail);
